// Service to handle post type limits and reset logic
export class PostTypeLimitsService {
  constructor() {
    this.limitsKey = 'postTypeLimits';
    this.resetDateKey = 'postTypeResetDate';
  }

  // Get current limits for a user
  getLimits(userId) {
    const limits = this.getStoredLimits();
    const resetDate = this.getResetDate();
    
    // Check if we need to reset limits (monthly reset)
    if (this.shouldResetLimits(resetDate)) {
      this.resetLimits();
      return this.getDefaultLimits();
    }

    return limits[userId] || this.getDefaultLimits();
  }

  // Update limits when a post is created
  updateLimits(userId, postType) {
    const limits = this.getStoredLimits();
    const userLimits = limits[userId] || this.getDefaultLimits();
    
    if (postType === 'standard' && userLimits.standard > 0) {
      userLimits.standard -= 1;
    } else if (postType === 'featured' && userLimits.featured > 0) {
      userLimits.featured -= 1;
    }

    limits[userId] = userLimits;
    this.setStoredLimits(limits);
    
    return userLimits;
  }

  // Get default limits (this would typically come from user's subscription plan)
  getDefaultLimits() {
    return {
      standard: 3, // Basic plan: 3 standard posts per month
      featured: 2, // Basic plan: 2 featured posts per month
      resetDays: 12 // Days until next reset
    };
  }

  // Get stored limits from localStorage
  getStoredLimits() {
    try {
      const stored = localStorage.getItem(this.limitsKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error parsing stored limits:', error);
      return {};
    }
  }

  // Set limits in localStorage
  setStoredLimits(limits) {
    try {
      localStorage.setItem(this.limitsKey, JSON.stringify(limits));
    } catch (error) {
      console.error('Error storing limits:', error);
    }
  }

  // Get reset date
  getResetDate() {
    try {
      const stored = localStorage.getItem(this.resetDateKey);
      return stored ? new Date(stored) : new Date();
    } catch (error) {
      console.error('Error parsing reset date:', error);
      return new Date();
    }
  }

  // Set reset date (next month)
  setResetDate() {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1); // First day of next month
    nextMonth.setHours(0, 0, 0, 0);
    
    try {
      localStorage.setItem(this.resetDateKey, nextMonth.toISOString());
    } catch (error) {
      console.error('Error storing reset date:', error);
    }
  }

  // Check if limits should be reset
  shouldResetLimits(resetDate) {
    const now = new Date();
    return now >= resetDate;
  }

  // Reset all limits
  resetLimits() {
    localStorage.removeItem(this.limitsKey);
    this.setResetDate();
  }

  // Calculate days until reset
  getDaysUntilReset() {
    const resetDate = this.getResetDate();
    const now = new Date();
    const diffTime = resetDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  // Check if a post type is available
  isPostTypeAvailable(userId, postType) {
    const limits = this.getLimits(userId);
    return limits[postType] > 0;
  }
}

// Create singleton instance
export const postTypeLimitsService = new PostTypeLimitsService();
